// Styles
import './style.css'
import 'primeicons/primeicons.css'

// Vue & Plugins
import { createApp } from 'vue'
import { createI18n, I18nOptions } from 'vue-i18n'
import { createPinia } from 'pinia'
import { VueQueryPlugin, VueQueryPluginOptions } from '@tanstack/vue-query'

// Components
import App from './App.vue'
import AppLayout from './common/components/layouts/AppLayout.vue'
import GuestLayout from './common/components/layouts/GuestLayout.vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import FormTextInputFloat from './common/components/inputs/FormTextInputFloat.vue'
import FormTextInput from './common/components/inputs/FormTextInput.vue'
import PrimaryButton from './common/components/buttons/PrimaryButton.vue'
import RefreshButton from './common/components/buttons/RefreshButton.vue'
import SecondaryButton from './common/components/buttons/SecondaryButton.vue'
import TweenNumber from './common/components/fields/TweenNumber.vue'

// Other
import enUS from './common/locales/en-US.json'
import router from './router'
import { registerPrimeVue } from './primeVue'
import { setupInterceptors } from './axiosInstance'

// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'

// Sentry
import * as Sentry from '@sentry/vue'
import { Ray } from 'node-ray/dist/web'
//import * as Spotlight from '@spotlightjs/spotlight'

Ray.useDefaultSettings({ enable: false })

// add font-awesome icons to the font-awesome library
library.add(fas)

const app = createApp(App)

// Components
app.component('AppLayout', AppLayout)
app.component('GuestLayout', GuestLayout)
app.component('FontAwesomeIcon', FontAwesomeIcon)
app.component('VueDatePicker', VueDatePicker)
app.component('FormTextInput', FormTextInput)
app.component('FormTextInputFloat', FormTextInputFloat)
app.component('PrimaryButton', PrimaryButton)
app.component('RefreshButton', RefreshButton)
app.component('SecondaryButton', SecondaryButton)
app.component('TweenNumber', TweenNumber)

// Plugins
// Router
app.use(router)
// VueQuery
const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: { queries: { retry: false } },
  },
}
app.use(VueQueryPlugin, vueQueryPluginOptions)
// Pinia
const pinia = createPinia()
app.use(pinia)
// PrimeVue
registerPrimeVue(app)

// i18n
const options: I18nOptions = {
  legacy: false,
  missingWarn: false,
  fallbackWarn: false,
  locale: 'en-US',
  fallbackLocale: 'en-US',
  messages: {
    'en-US': enUS,
  },
  numberFormats: {
    'en-US': {
      currency: {
        style: 'currency',
        currencyDisplay: 'symbol',
        currency: 'USD',
      },

      decimal: {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      },
    }
  }
}
const i18n = createI18n<false, typeof options>(options)
app.use(i18n)

setupInterceptors(router)

// Sentry
Sentry.init({
  app: app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  logErrors: true, // Allow vue to call logError internally, showing errors in the developer console // TODO: Review if this functionality is desirable in production
})

if (import.meta.env.DEV) {
  // Spotlight.init()
}

app.mount('#app')